/**
 * Page-level JSON-LD for the programmatic API detail pages (/api/:apiId). Emits a
 * Service node (the API offering) plus a BreadcrumbList, so the ~20 catalog pages
 * get rich-result eligibility and citable structured data. AnyAPI is always the
 * provider (referenced by @id from orgSchema); upstream backends are never named.
 * Mirrors the blogSchemaGraph pattern (see pages/blog/BlogPostPage.tsx).
 */
import type { ApiDetail } from '../../features/catalog/model';
import { cheapest } from '../catalog';
import { aggregateOffer } from './aggregateOffer';
import { localizedUrl } from '../../i18n/localeHead';
import { breadcrumbList, catalogStep } from './breadcrumb';
import { ORG_ID } from './orgSchema';

/**
 * What one request costs, across a platform's endpoints.
 *
 * `AggregateOffer` rather than a single `Offer` because the endpoints are priced
 * independently and a lone number would have to pick one and call it "the"
 * price. Amounts are per request because that is the only denomination a flat
 * endpoint and a metered one share, and a range built from both would otherwise
 * be adding results to requests. The per-endpoint pages quote each lane in the
 * unit it bills; every figure here comes from `cheapest`, so the range cannot
 * drift from the endpoints it spans.
 */
function priceRange(detail: ApiDetail) {
  return aggregateOffer(
    detail.endpoints
      .filter((endpoint) => endpoint.providers.length > 0)
      .map((endpoint) => cheapest(endpoint).price.perRequestUsd),
  );
}

/** Service + BreadcrumbList graph for one API detail page. */
export function apiSchemaGraph(detail: ApiDetail, apiId: string) {
  const url = localizedUrl(`/api/${apiId}`);
  const offers = priceRange(detail);
  const service = {
    '@type': 'Service',
    '@id': `${url}#service`,
    name: `${detail.name} API`,
    description: detail.blurb,
    url,
    provider: { '@id': ORG_ID },
    serviceType: 'Web scraping API',
    areaServed: 'Worldwide',
    ...(offers ? { offers } : {}),
  };
  const breadcrumb = breadcrumbList(catalogStep(), { name: detail.name, url });
  return { '@context': 'https://schema.org', '@graph': [service, breadcrumb] };
}
